import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

// Declare global grecaptcha for TypeScript
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export default function ContactSection() {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Replace with your actual reCAPTCHA site key
  const RECAPTCHA_SITE_KEY = '6LeIk70rAAAAAD_A7tKDeMxeEPEd7xky_7adM3I-';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Initialize reCAPTCHA when ready
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        setRecaptchaReady(true);
      });
    }
  }, []);

  const executeRecaptcha = async (): Promise<string | null> => {
    if (!recaptchaReady || !window.grecaptcha) {
      console.error('reCAPTCHA not ready');
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: 'contact_form'
      });
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const projectDetails = formData.get('project') as string;
    if (projectDetails && projectDetails.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    // Execute reCAPTCHA verification
    const recaptchaToken = await executeRecaptcha();
    if (!recaptchaToken) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
    const ref = await addDoc(collection(db, 'form-data'), {
      name: formData.get('name') || '',
      email: formData.get('email') || '',
      company: formData.get('company') || '',
      phone: formData.get('phone') || '',
      project: projectDetails || '',
      recaptchaToken: recaptchaToken || '',
      createdAt: serverTimestamp(),
    });

    console.log('✅ Document written with ID:', ref.id);
    setSubmitStatus('success');
    form.reset();
  } catch (err: any) {
    console.error('❌ Firestore addDoc failed:', err);
    console.error('code:', err?.code, 'message:', err?.message);
    setSubmitStatus('error');
  } finally {
    setIsSubmitting(false);
  }
  };

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className={`py-20 bg-blue-100 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-blue-500 font-medium">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className={`space-y-8 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="flex items-start gap-4">
              <div className="bg-blue-200 p-3 rounded-lg">
                <i className="ri-map-pin-line w-6 h-6 flex items-center justify-center text-blue-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Address</h3>
                <p className="text-gray-600">{t('contact.address')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-blue-200 p-3 rounded-lg">
                <i className="ri-phone-line w-6 h-6 flex items-center justify-center text-blue-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Phone</h3>
                <p className="text-gray-600">{t('contact.phone')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-blue-200 p-3 rounded-lg">
                <i className="ri-mail-line w-6 h-6 flex items-center justify-center text-blue-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Email</h3>
                <p className="text-gray-600">{t('contact.email')}</p>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d968.1042264175995!2d100.3740005935493!3d13.933764389143871!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e28f26c3b8ac2b%3A0x634d10d292413d60!2sJ.S.P.M%20Engineering%20Co.%2C%20Ltd.!5e0!3m2!1sth!2sth!4v1757063706303!5m2!1sth!2sth"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`bg-blue-50 p-8 rounded-lg transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.company')}
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.project')}
                </label>
                <textarea
                  name="project"
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="Maximum 500 characters"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !recaptchaReady}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white py-3 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : t('contact.form.submit')}
              </button>

              {/* reCAPTCHA notice */}
              <div className="mt-3 text-xs text-gray-500 text-center">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Terms of Service
                </a>{' '}
                apply.
              </div>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-lg">
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
                  Failed to send message. Please check your input and try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
